import VehicleImage from '../components/VehicleImage';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vehicleApi, bookingApi, damageApi, customerApi, qrApi, tireApi, formatApiError } from '../services/api';
import {
  Car, ArrowLeft, PencilSimple, ChartLineUp, GasPump, Gear,
  Users as SeatsIcon, Calendar, MapPin, CurrencyEur, Wrench,
  IdentificationCard, Warning, ArrowRight, QrCode, Printer, DownloadSimple,
  UploadSimple, FilePdf, Eye, Trash,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import VehicleRevenueDialog from '../components/VehicleRevenueDialog';
import CleaningPanel from '../components/CleaningPanel';
import EditableNumber from '../components/EditableNumber';

const STATUS_LABELS = {
  available: 'Verfügbar',
  rented: 'Vermietet',
  maintenance: 'Wartung',
  cleaning: 'Reinigung',
  blocked: 'Gesperrt',
};

const STATUS_STYLES = {
  available: 'badge-success',
  rented: 'badge-warning',
  maintenance: 'badge-neutral',
  cleaning: 'bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold',
  blocked: 'badge-neutral',
};

const formatEur = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const formatDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('de-DE'); } catch (_) { return s.slice(0, 10); }
};

const Field = ({ label, value, mono = false }) => (
  <div>
    <p className="overline text-[#8A94A6] mb-1">{label}</p>
    <p className={`text-sm text-[#111418] ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
  </div>
);

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [damages, setDamages] = useState([]);
  const [customerMap, setCustomerMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRevenue, setShowRevenue] = useState(false);
  const [registrationUploading, setRegistrationUploading] = useState(false);
  const [showRegistrationDocument, setShowRegistrationDocument] = useState(false);
  const [tireDialog, setTireDialog] = useState({ open: false, new_type: 'winter', change_date: new Date().toISOString().slice(0, 10), mileage: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, bRes, dRes] = await Promise.all([
        vehicleApi.getOne(id),
        bookingApi.getAll({ vehicle_id: id }),
        damageApi.getAll({ vehicle_id: id }),
      ]);
      setVehicle(vRes.data);
      const bs = bRes.data || [];
      setBookings(bs);
      setDamages(dRes.data || []);
      // Resolve customer names
      const ids = [...new Set(bs.map((b) => b.customer_id).filter(Boolean))];
      if (ids.length) {
        const all = await customerApi.getAll();
        const map = {};
        (all.data || []).forEach((c) => { map[c.id] = c; });
        setCustomerMap(map);
      }
    } catch (err) {
      toast.error(formatApiError(err) || 'Fahrzeug konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleRegistrationUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Bitte nur PDF, JPG oder PNG hochladen');
      event.target.value = '';
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      toast.error('Die Datei darf maximal 15 MB groß sein');
      event.target.value = '';
      return;
    }

    setRegistrationUploading(true);
    try {
      await vehicleApi.uploadRegistrationDocument(id, file);
      const res = await vehicleApi.getOne(id);
      setVehicle(res.data);
      toast.success('Fahrzeugschein wurde hochgeladen');
    } catch (err) {
      toast.error(formatApiError(err) || 'Fahrzeugschein konnte nicht hochgeladen werden');
    } finally {
      setRegistrationUploading(false);
      event.target.value = '';
    }
  };

  const handleRegistrationShare = async () => {
    if (!vehicle?.registration_document_url) return;

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${vehicle.registration_document_url}`;
      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) {
        throw new Error('Datei konnte nicht geladen werden');
      }

      const blob = await response.blob();
      const filename = vehicle.registration_document_name || 'Fahrzeugschein';
      const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `Fahrzeugschein ${vehicle.license_plate || ''}`,
          text: `Fahrzeugschein ${vehicle.brand || ''} ${vehicle.model || ''} ${vehicle.license_plate || ''}`.trim(),
          files: [file],
        });
      } else {
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(downloadUrl);
        toast.info('Die Datei wurde heruntergeladen und kann jetzt weitergeleitet werden');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error('Fahrzeugschein konnte nicht geteilt werden');
      }
    }
  };

  const handleRegistrationDelete = async () => {
    if (!window.confirm('Fahrzeugschein wirklich löschen?')) return;

    setRegistrationUploading(true);
    try {
      await vehicleApi.deleteRegistrationDocument(id);
      const res = await vehicleApi.getOne(id);
      setVehicle(res.data);
      toast.success('Fahrzeugschein wurde gelöscht');
    } catch (err) {
      toast.error(formatApiError(err) || 'Fahrzeugschein konnte nicht gelöscht werden');
    } finally {
      setRegistrationUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="card-swiss h-32 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card-swiss h-48 skeleton" />
          <div className="card-swiss h-48 skeleton" />
          <div className="card-swiss h-48 skeleton" />
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="p-6">
        <p className="text-[#525866]">Fahrzeug nicht gefunden.</p>
        <Button onClick={() => navigate('/vehicles')} variant="outline" className="mt-3">
          <ArrowLeft size={16} className="mr-2" /> Zurück
        </Button>
      </div>
    );
  }

  // KPI from bookings
  const totalRevenue = bookings
    .filter((b) => b.status !== 'cancelled' && b.status !== 'canceled')
    .reduce((sum, b) => sum + Number(b.price_info?.total_price || 0), 0);
  const totalDays = bookings
    .filter((b) => b.status !== 'cancelled' && b.status !== 'canceled')
    .reduce((sum, b) => sum + Number(b.price_info?.days || 0), 0);
  const activeBookings = bookings.filter((b) => b.status === 'active' || b.status === 'confirmed').length;

  return (
    <div className="p-6 space-y-6" data-testid="vehicle-detail-page">
      {/* Top bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => navigate('/vehicles')} data-testid="back-to-vehicles">
            <ArrowLeft weight="bold" size={18} className="mr-1" /> Zurück
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111418]">
              {vehicle.brand} {vehicle.model}
            </h1>
            <p className="text-sm text-[#525866] font-mono flex items-center gap-3 flex-wrap">
              <span>{vehicle.license_plate}</span>
              <span className="px-2 py-0.5 bg-[#F1F2F4] rounded text-[#111418] text-xs flex items-center gap-1">
                FZ-Nr.:
                <EditableNumber
                  value={vehicle.vehicle_number}
                  label="Fahrzeugnummer"
                  testId="vehicle-number"
                  small
                  onSave={async (newVal) => {
                    const res = await vehicleApi.update(vehicle.id, { vehicle_number: newVal });
                    setVehicle(res.data);
                  }}
                />
              </span>
            </p>
          </div>
          <span className={`badge ${STATUS_STYLES[vehicle.status] || 'badge-neutral'}`}>
            {STATUS_LABELS[vehicle.status] || vehicle.status}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowRevenue(true)} data-testid="open-revenue-btn">
            <ChartLineUp weight="bold" size={16} className="mr-2" /> Umsatz pro Monat
          </Button>
          <Button onClick={() => navigate(`/vehicles?edit=${vehicle.id}`)} className="btn-primary">
            <PencilSimple weight="bold" size={16} className="mr-2" /> Bearbeiten
          </Button>
        </div>
      </div>

      {/* Hero: image + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-swiss overflow-hidden">
          <div className="aspect-video bg-[#F7F8FA]">
            {vehicle.image_url ? (
              <VehicleImage path={vehicle.image_url}
                alt={`${vehicle.brand} ${vehicle.model}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Car weight="thin" size={64} className="text-[#8A94A6]" />
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="card-swiss p-4">
            <p className="overline text-[#8A94A6]">Tagespreis</p>
            <p className="text-2xl font-bold text-[#E60000]" data-testid="kpi-daily-rate">{formatEur(vehicle.daily_rate)}</p>
          </div>
          <div className="card-swiss p-4">
            <p className="overline text-[#8A94A6]">Buchungen</p>
            <p className="text-2xl font-bold text-[#111418]" data-testid="kpi-bookings">{bookings.length}</p>
            <p className="text-xs text-[#8A94A6]">{activeBookings} aktiv/bestätigt</p>
          </div>
          <div className="card-swiss p-4">
            <p className="overline text-[#8A94A6]">Vermietete Tage</p>
            <p className="text-2xl font-bold text-[#111418]" data-testid="kpi-days">{totalDays}</p>
          </div>
          <div className="card-swiss p-4">
            <p className="overline text-[#8A94A6]">Gesamt-Umsatz (Soll)</p>
            <p className="text-2xl font-bold text-[#009951]" data-testid="kpi-revenue">{formatEur(totalRevenue)}</p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Stammdaten */}
        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <Car weight="fill" size={16} className="text-[#E60000]" /> Stammdaten
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Marke" value={vehicle.brand} />
            <Field label="Modell" value={vehicle.model} />
            <Field label="Kennzeichen" value={vehicle.license_plate} mono />
            <Field label="Baujahr" value={vehicle.year} />
            <Field label="Farbe" value={vehicle.color} />
            <Field label="Kategorie" value={vehicle.category} />
            <Field label="Fahrzeugtyp" value={
              vehicle.vehicle_type
                ? ({
                    pkw: 'PKW', transporter: 'Transporter', lkw: 'LKW', bus: 'Bus',
                    wohnmobil: 'Wohnmobil', anhaenger: 'Anhänger', motorrad: 'Motorrad', sonstige: 'Sonstige'
                  }[vehicle.vehicle_type] || vehicle.vehicle_type)
                : null
            } />
            <Field label="Eigentumsart" value={
              vehicle.ownership_type
                ? ({ eigen: 'Eigene', lieferant: 'Lieferant / Leasing', kunden: 'Kundenüberlassung' }[vehicle.ownership_type] || vehicle.ownership_type)
                : 'Eigene'
            } />
          </div>
        </div>

        {/* Technische Daten */}
        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <Gear weight="fill" size={16} className="text-[#E60000]" /> Technik
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Kraftstoff" value={vehicle.fuel_type} />
            <Field label="Getriebe" value={vehicle.transmission} />
            <Field label="Sitze" value={vehicle.seats} />
            <Field label="Führerscheinklasse" value={vehicle.required_license_class} />
          </div>
        </div>

        {/* Preise */}
        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <CurrencyEur weight="fill" size={16} className="text-[#E60000]" /> Preise & Tarife
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tagespreis" value={formatEur(vehicle.daily_rate)} />
            <Field label="Wochenend-Tarif" value={formatEur(vehicle.weekend_rate)} />
            <Field label="Wochen-Tarif" value={formatEur(vehicle.weekly_rate)} />
            <Field label="Monats-Tarif" value={formatEur(vehicle.monthly_rate)} />
            <Field label="Standard-Kaution" value={formatEur(vehicle.default_deposit)} />
            <Field label="Selbstbehalt" value={formatEur(vehicle.insurance_deductible)} />
          </div>
        </div>

        {/* Notizen */}
        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <IdentificationCard weight="fill" size={16} className="text-[#E60000]" /> Sonstiges
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Erstellt am" value={formatDate(vehicle.created_at)} />
            <Field label="Zuletzt aktualisiert" value={formatDate(vehicle.updated_at)} />
          </div>
          {vehicle.notes && (
            <div className="mt-4">
              <p className="overline text-[#8A94A6] mb-1">Notizen</p>
              <p className="text-sm text-[#111418] whitespace-pre-wrap">{vehicle.notes}</p>
            </div>
          )}
        </div>

        {/* QR-Code Karte */}
        <div className="card-swiss p-5" data-testid="vehicle-qr-card">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <QrCode weight="fill" size={16} className="text-[#E60000]" /> QR-Code
          </h2>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="bg-white border border-[#E2E4E9] rounded-sm p-2 flex-shrink-0">
              <img
                src={qrApi.vehiclePngUrl(vehicle.id, 8)}
                alt={`QR-Code ${vehicle.license_plate}`}
                className="w-32 h-32 block"
                data-testid="vehicle-qr-image"
              />
            </div>
            <div className="flex-1 min-w-0 space-y-2 w-full">
              <p className="text-xs text-[#525866]">
                Drucke und klebe diesen QR-Code aufs Fahrzeug. Mit der App scannen → Schnell-Aktionen wie Rückgabe, Schaden, km-Stand.
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href={qrApi.vehiclePngUrl(vehicle.id, 16)}
                  download={`qr-${vehicle.license_plate}.png`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#E2E4E9] rounded-sm hover:bg-[#FAFAFB]"
                  data-testid="vehicle-qr-download-btn"
                >
                  <DownloadSimple size={14} /> PNG
                </a>
                <a
                  href={qrApi.vehicleLabelPdfUrl(vehicle.id, 'sticker')}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#E2E4E9] rounded-sm hover:bg-[#FAFAFB]"
                  data-testid="vehicle-qr-sticker-btn"
                >
                  <Printer size={14} /> Klebeetikett
                </a>
                <a
                  href={qrApi.vehicleLabelPdfUrl(vehicle.id, 'a4')}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#E2E4E9] rounded-sm hover:bg-[#FAFAFB]"
                  data-testid="vehicle-qr-a4-btn"
                >
                  <Printer size={14} /> A4-Seite
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fahrzeug-Stammdaten erweitert: FIN, TÜV, Inspektion, Versicherung, Service */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <IdentificationCard weight="fill" size={16} className="text-[#E60000]" /> Fahrzeug-Identifikation
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Fahrgestellnummer (FIN)" value={vehicle.vin} mono />
            <Field label="Erstzulassung" value={formatDate(vehicle.first_registration_date)} />
            <Field label="Aktueller Kilometerstand" value={vehicle.current_mileage ? `${Number(vehicle.current_mileage).toLocaleString('de-DE')} km` : '—'} />
            <Field label="Garantie-Ablauf" value={formatDate(vehicle.warranty_until)} />
          </div>
        </div>

        {/* Fahrzeugschein */}
        <div className="card-swiss p-5" data-testid="vehicle-registration-document">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <FilePdf weight="fill" size={16} className="text-[#E60000]" /> Fahrzeugschein
          </h2>

          {vehicle.registration_document_url ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                <FilePdf size={32} className="text-[#E60000] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#111418] truncate">
                    {vehicle.registration_document_name || 'Fahrzeugschein'}
                  </p>
                  <p className="text-xs text-[#8A94A6]">
                    PDF / Bild
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowRegistrationDocument(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-[#E2E4E9] rounded-sm hover:bg-[#FAFAFB]"
                >
                  <Eye size={15} /> Ansehen
                </button>

                <label className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-[#E2E4E9] rounded-sm hover:bg-[#FAFAFB] cursor-pointer ${registrationUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <UploadSimple size={15} />
                  {registrationUploading ? 'Bitte warten…' : 'Ersetzen'}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    className="hidden"
                    onChange={handleRegistrationUpload}
                    disabled={registrationUploading}
                  />
                </label>

                <button
                  type="button"
                  onClick={handleRegistrationDelete}
                  disabled={registrationUploading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#E60000] border border-[#E2E4E9] rounded-sm hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash size={15} /> Löschen
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#525866]">
                Noch kein Fahrzeugschein hinterlegt. PDF, JPG oder PNG bis maximal 15 MB.
              </p>

              <label className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[#111418] text-white rounded-sm hover:bg-black cursor-pointer ${registrationUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <UploadSimple size={16} />
                {registrationUploading ? 'Wird hochgeladen…' : 'Fahrzeugschein hochladen'}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={handleRegistrationUpload}
                  disabled={registrationUploading}
                />
              </label>
            </div>
          )}
        </div>

        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <Wrench weight="fill" size={16} className="text-[#E60000]" /> TÜV & Inspektion
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nächste HU fällig" value={formatDate(vehicle.tuev_date)} />
            <Field label="Prüfstelle" value={vehicle.tuev_inspector} />
            <Field label="Nächste Inspektion" value={formatDate(vehicle.next_inspection_date)} />
          </div>
        </div>

        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <IdentificationCard weight="fill" size={16} className="text-[#E60000]" /> Versicherung
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Gesellschaft" value={vehicle.insurance_company} />
            <Field label="Vertragsnummer" value={vehicle.insurance_policy_number} mono />
          </div>
        </div>

        <div className="card-swiss p-5">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
            <Wrench weight="fill" size={16} className="text-[#E60000]" /> Service & Verschleiß
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Ölwechsel-Intervall" value={vehicle.oil_change_interval_km ? `${Number(vehicle.oil_change_interval_km).toLocaleString('de-DE')} km` : (vehicle.oil_change_interval_months ? `${vehicle.oil_change_interval_months} Monate` : '—')} />
            <Field label="Letzter Ölwechsel" value={vehicle.last_oil_change_date ? `${formatDate(vehicle.last_oil_change_date)}${vehicle.last_oil_change_mileage ? ' · ' + Number(vehicle.last_oil_change_mileage).toLocaleString('de-DE') + ' km' : ''}` : '—'} />
            <Field label="Service-Intervall" value={vehicle.service_interval_km ? `${Number(vehicle.service_interval_km).toLocaleString('de-DE')} km` : '—'} />
            <Field label="Letzter Service" value={vehicle.last_service_date ? `${formatDate(vehicle.last_service_date)}${vehicle.last_service_mileage ? ' · ' + Number(vehicle.last_service_mileage).toLocaleString('de-DE') + ' km' : ''}` : '—'} />
            <Field label="Aktuell montiert" value={{summer: 'Sommerreifen', winter: 'Winterreifen', all_season: 'Ganzjahresreifen'}[vehicle.current_tire_type] || '—'} />
            <Field label="Sommerreifen (letzter Wechsel)" value={formatDate(vehicle.last_tire_change_summer_date)} />
            <Field label="Winterreifen (letzter Wechsel)" value={formatDate(vehicle.last_tire_change_winter_date)} />
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <Button size="sm" variant="outline" data-testid="tire-change-btn" onClick={() => setTireDialog({ open: true, new_type: vehicle.current_tire_type === 'winter' ? 'summer' : 'winter', change_date: new Date().toISOString().slice(0, 10), mileage: '', notes: '' })}>
              <Wrench weight="bold" size={14} className="mr-1" /> Reifenwechsel erfassen
            </Button>
            <Button size="sm" variant="outline" data-testid="tire-notify-btn" onClick={async () => {
              try {
                const r = await tireApi.notifyCustomer(vehicle.id);
                toast.success('Erinnerung als Brief-Entwurf angelegt', {
                  description: `An: ${r.data.customer_name} · ${r.data.customer_email || 'ohne E-Mail'}`,
                  duration: 8000,
                });
              } catch (e) { toast.error(formatApiError(e)); }
            }}>
              <ArrowRight weight="bold" size={14} className="mr-1" /> Kunde informieren (Langzeit)
            </Button>
          </div>
        </div>
      </div>

      {/* Bookings list */}
      <div className="card-swiss p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold tracking-wide text-[#111418] flex items-center gap-2">
            <Calendar weight="fill" size={16} className="text-[#E60000]" />
            Buchungen ({bookings.length})
          </h2>
        </div>
        {bookings.length === 0 ? (
          <p className="text-sm text-[#8A94A6]">Noch keine Buchungen für dieses Fahrzeug.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8FA] text-[#525866]">
                <tr>
                  <th className="text-left p-2 font-semibold">Buchung</th>
                  <th className="text-left p-2 font-semibold">Kunde</th>
                  <th className="text-left p-2 font-semibold">Von</th>
                  <th className="text-left p-2 font-semibold">Bis</th>
                  <th className="text-right p-2 font-semibold">Tage</th>
                  <th className="text-right p-2 font-semibold">Preis</th>
                  <th className="text-left p-2 font-semibold">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {bookings
                  .slice()
                  .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''))
                  .map((b) => {
                    const c = customerMap[b.customer_id];
                    const cname = c ? (c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()) : '—';
                    return (
                      <tr key={b.id} className="border-t border-[#E2E4E9] hover:bg-[#FAFAFB]">
                        <td className="p-2 font-mono text-xs">{b.booking_number || b.id?.slice(0, 8)}</td>
                        <td className="p-2">{cname}</td>
                        <td className="p-2">{formatDate(b.start_date)}</td>
                        <td className="p-2">{formatDate(b.end_date)}</td>
                        <td className="p-2 text-right tabular-nums">{b.price_info?.days || '—'}</td>
                        <td className="p-2 text-right tabular-nums">{formatEur(b.price_info?.total_price)}</td>
                        <td className="p-2">
                          <span className="text-xs px-2 py-1 rounded bg-[#F7F8FA]">{b.status}</span>
                        </td>
                        <td className="p-2 text-right">
                          <Link to={`/bookings/${b.id}`} className="text-[#E60000] hover:underline text-xs flex items-center justify-end gap-1">
                            Details <ArrowRight size={12} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Damages */}
      <div className="card-swiss p-5">
        <h2 className="text-sm font-bold tracking-wide text-[#111418] mb-4 flex items-center gap-2">
          <Warning weight="fill" size={16} className="text-[#FFB800]" />
          Schäden ({damages.length})
        </h2>
        {damages.length === 0 ? (
          <p className="text-sm text-[#8A94A6]">Keine Schäden für dieses Fahrzeug erfasst.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8FA] text-[#525866]">
                <tr>
                  <th className="text-left p-2 font-semibold">Datum</th>
                  <th className="text-left p-2 font-semibold">Beschreibung</th>
                  <th className="text-right p-2 font-semibold">Schaden</th>
                  <th className="text-left p-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {damages.map((d) => (
                  <tr key={d.id} className="border-t border-[#E2E4E9]">
                    <td className="p-2">{formatDate(d.reported_date || d.created_at)}</td>
                    <td className="p-2">{d.description || '—'}</td>
                    <td className="p-2 text-right tabular-nums">{formatEur(d.repair_cost || d.estimated_cost)}</td>
                    <td className="p-2">
                      <span className="text-xs px-2 py-1 rounded bg-[#F7F8FA]">{d.status || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reinigungs-Checkliste */}
      {(vehicle.status === 'cleaning' || vehicle.cleaning_state) && (
        <div className="mt-6">
          <CleaningPanel vehicleId={vehicle.id} onChanged={load} />
        </div>
      )}

      {/* Revenue dialog */}
      <VehicleRevenueDialog
        open={showRevenue}
        onClose={() => setShowRevenue(false)}
        vehicleId={vehicle.id}
        vehicleLabel={`${vehicle.brand} ${vehicle.model} (${vehicle.license_plate})`}
      />

      {/* Fahrzeugschein Vorschau */}
      <Dialog open={showRegistrationDocument} onOpenChange={setShowRegistrationDocument}>
        <DialogContent className="max-w-4xl w-[95vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePdf size={20} className="text-[#E60000]" />
              Fahrzeugschein – {vehicle.license_plate}
            </DialogTitle>
          </DialogHeader>

          {vehicle.registration_document_url && (
            <div className="space-y-4">
              <div className="bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm overflow-hidden min-h-[55vh] flex items-center justify-center">
                {/\.(jpg|jpeg|png)$/i.test(vehicle.registration_document_url) ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${vehicle.registration_document_url}`}
                    alt="Fahrzeugschein"
                    className="max-w-full max-h-[65vh] object-contain"
                  />
                ) : (
                  <iframe
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${vehicle.registration_document_url}`}
                    title="Fahrzeugschein"
                    className="w-full h-[65vh] border-0"
                  />
                )}
              </div>

              <p className="text-xs text-[#8A94A6] truncate">
                {vehicle.registration_document_name || 'Fahrzeugschein'}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowRegistrationDocument(false)}
            >
              Schließen
            </Button>

            <Button
              type="button"
              onClick={handleRegistrationShare}
              className="btn-primary"
            >
              <UploadSimple size={16} className="mr-2" />
              Weiterleiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tire change dialog */}
      <Dialog open={tireDialog.open} onOpenChange={(o) => setTireDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reifenwechsel erfassen</DialogTitle></DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const r = await tireApi.recordChange(vehicle.id, {
                  new_type: tireDialog.new_type,
                  change_date: tireDialog.change_date,
                  mileage: tireDialog.mileage ? Number(tireDialog.mileage) : null,
                  notes: tireDialog.notes || null,
                });
                setVehicle(r.data);
                setTireDialog(d => ({ ...d, open: false }));
                toast.success('Reifenwechsel erfasst');
              } catch (err) { toast.error(formatApiError(err)); }
            }}
            className="space-y-4"
            data-testid="tire-change-form"
          >
            <div>
              <Label className="overline">Neuer Reifentyp</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { k: 'summer', l: 'Sommer' },
                  { k: 'winter', l: 'Winter' },
                  { k: 'all_season', l: 'Ganzjahres' },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setTireDialog(d => ({ ...d, new_type: t.k }))}
                    data-testid={`tire-type-${t.k}`}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                      tireDialog.new_type === t.k ? 'bg-[#111418] text-white border-[#111418]' : 'bg-white text-[#525866] border-[#E1E4E8] hover:bg-[#F5F7FA]'
                    }`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="overline">Datum</Label>
              <Input type="date" value={tireDialog.change_date} onChange={(e) => setTireDialog(d => ({ ...d, change_date: e.target.value }))} required className="input-swiss" data-testid="tire-date" />
            </div>
            <div>
              <Label className="overline">Aktueller Kilometerstand (optional)</Label>
              <Input type="number" min="0" value={tireDialog.mileage} onChange={(e) => setTireDialog(d => ({ ...d, mileage: e.target.value }))} className="input-swiss" data-testid="tire-mileage" />
            </div>
            <div>
              <Label className="overline">Notiz (optional)</Label>
              <Input value={tireDialog.notes} onChange={(e) => setTireDialog(d => ({ ...d, notes: e.target.value }))} className="input-swiss" data-testid="tire-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTireDialog(d => ({ ...d, open: false }))}>Abbrechen</Button>
              <Button type="submit" className="btn-primary" data-testid="tire-save-btn">Erfassen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleDetail;
